/**
 * The left sidebar: 72 px icon rail + 279 px workspace pane (spec M0-F4 §7.3–§7.4).
 *
 * Expanded the two together measure 351 px (`[uij:shell-01#sidebar]` w=351), collapsed 72 px
 * (`[uij:shell-02#sidebar]`), and the same control does both — `«` in the pane header, `»` at
 * the top of the rail (S3).
 */
import { Icon, IconButton, PageErrorState, Search, Skeleton, Tooltip, icons } from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useSidebarData, type ShellCapabilities } from '../../modules/shell/bootstrap';
import { clampSidebarWidth, SIDEBAR_WIDTH_DEFAULT, usePreference } from '../../modules/shell/preferences';
import { useShellStore } from '../../modules/shell/store';
import {
  buildTree,
  flattenTree,
  pathToLeaf,
  sortTreeByName,
  type SidebarPayload,
} from '../../modules/shell/tree';
import { workspaceUrl } from '../../modules/shell/urls';
import { AddMenu } from '../../modules/boards/AddMenu';
import { WorkspaceMenu } from '../../modules/boards/WorkspaceMenu';
import { ContentSectionMenu } from '../../modules/boards/ContentSectionMenu';
import { FavoritesPane } from '../../modules/boards/FavoritesPane';
import { RailMoreMenu } from '../../modules/boards/RailMoreMenu';
import { IconRail } from './IconRail';
import { SidebarTree } from './SidebarTree';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

export interface SidebarProps {
  workspaceId: string | null;
  /** The board / doc / dashboard id in the URL, so the tree can reveal and highlight it (S2). */
  selectedEntityId: string | null;
  capabilities: ShellCapabilities;
  /**
   * Whether the pane is on screen at all. Below 1024 px it is hidden until the rail's `»` is
   * used, and then it floats over main with a scrim (§7.10) — but the **rail always stays**,
   * which is what "rail only" in the breakpoint table means.
   */
  paneVisible: boolean;
  /** True while the pane is floating rather than pinned. */
  asOverlay: boolean;
  /** Reveals the pane at a viewport where it is not pinned. */
  onRequestPane: () => void;
  onDismissOverlay: () => void;
}

export function Sidebar({
  workspaceId,
  selectedEntityId,
  capabilities,
  paneVisible,
  asOverlay,
  onRequestPane,
  onDismissOverlay,
}: SidebarProps) {
  const [expanded, setExpanded] = usePreference('shell.sidebarExpanded');
  const [width, setWidth] = usePreference('shell.sidebarWidth');
  const [expandedFolderIds, setExpandedFolderIds] = usePreference('shell.expandedFolderIds');
  /** B10: the Content section's `Sort by alphabetical order` toggle `[c:sidebar-content-menu]`. */
  const [contentSort, setContentSort] = usePreference('shell.contentSort');
  const sortAlphabetically = contentSort === 'alphabetical';

  const railSlot = useShellStore((state) => state.railSlot);
  const setRailSlot = useShellStore((state) => state.setRailSlot);
  const treeFilter = useShellStore((state) => state.treeFilter);
  const treeFilterOpen = useShellStore((state) => state.treeFilterOpen);
  const setTreeFilter = useShellStore((state) => state.setTreeFilter);
  const setTreeFilterOpen = useShellStore((state) => state.setTreeFilterOpen);
  const setResizing = useShellStore((state) => state.setResizing);

  const [addMenuAt, setAddMenuAt] = useState<{ x: number; y: number } | null>(null);
  const collapseAllFolders = useCallback(() => {
    if (workspaceId) setExpandedFolderIds({ ...expandedFolderIds, [workspaceId]: [] });
  }, [workspaceId, expandedFolderIds, setExpandedFolderIds]);
  /** B10: the pane's `···` and the Content section's `···`, both stubs until this round. */
  const [paneMenuAt, setPaneMenuAt] = useState<{ x: number; y: number } | null>(null);
  const [contentMenuAt, setContentMenuAt] = useState<{ x: number; y: number } | null>(null);
  const [moreMenuAt, setMoreMenuAt] = useState<{ x: number; y: number } | null>(null);

  const sidebar = useSidebarData(workspaceId);
  const payload = sidebar.data as SidebarPayload | undefined;

  const { nodes, droppedEdges } = useMemo(
    () => (payload ? buildTree(payload) : { nodes: [], droppedEdges: [] as string[] }),
    [payload],
  );

  // E5: a `parent_folder_id` loop is logged once, not per render and never as a crash.
  const loggedCycles = useRef(false);
  useEffect(() => {
    if (droppedEdges.length === 0 || loggedCycles.current) return;
    loggedCycles.current = true;
    console.warn(`Sidebar: dropped ${droppedEdges.length} folder back-edge(s): ${droppedEdges.join(', ')}`);
  }, [droppedEdges]);

  const openFolderIds = useMemo(
    () => new Set(workspaceId ? (expandedFolderIds[workspaceId] ?? []) : []),
    [expandedFolderIds, workspaceId],
  );

  // S2: navigating to a board auto-expands the path to it.
  useEffect(() => {
    if (!workspaceId || !selectedEntityId || nodes.length === 0) return;
    const path = pathToLeaf(nodes, selectedEntityId);
    if (path.length === 0) return;
    const current = expandedFolderIds[workspaceId] ?? [];
    const missing = path.filter((id) => !current.includes(id));
    if (missing.length === 0) return;
    setExpandedFolderIds({ ...expandedFolderIds, [workspaceId]: [...current, ...missing] });
  }, [workspaceId, selectedEntityId, nodes, expandedFolderIds, setExpandedFolderIds]);

  const rows = useMemo(
    () =>
      flattenTree(sortAlphabetically ? sortTreeByName(nodes) : nodes, {
        expandedFolderIds: openFolderIds,
        ...(treeFilterOpen && treeFilter ? { filter: treeFilter } : {}),
      }),
    [nodes, openFolderIds, treeFilter, treeFilterOpen, sortAlphabetically],
  );

  // S1: expanding/collapsing a folder writes `shell.expandedFolderIds[workspaceId]`.
  const toggleFolder = useCallback(
    (folderId: string) => {
      if (!workspaceId) return;
      const current = expandedFolderIds[workspaceId] ?? [];
      const next = current.includes(folderId)
        ? current.filter((id) => id !== folderId)
        : [...current, folderId];
      setExpandedFolderIds({ ...expandedFolderIds, [workspaceId]: next });
    },
    [workspaceId, expandedFolderIds, setExpandedFolderIds],
  );

  /** S4: drag the pane's right border, clamped 240–520; double-click resets to 279. */
  const startResize = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = width;
      setResizing(true);

      const onMove = (move: PointerEvent) => setWidth(clampSidebarWidth(startWidth + move.clientX - startX));
      const onUp = () => {
        setResizing(false);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [width, setWidth, setResizing],
  );

  if (!paneVisible) {
    const reveal = () => {
      // At a pinned viewport the pane is a preference; at a narrow one it is an overlay.
      if (!expanded) setExpanded(true);
      onRequestPane();
    };
    return (
      <IconRail
        slot={railSlot}
        onSelect={(slot) => {
          setRailSlot(slot);
          reveal();
        }}
        collapsed
        onExpand={reveal}
        capabilities={capabilities}
      />
    );
  }

  const paneTitle = railSlot === 'favorites' ? 'Favorites' : railSlot === 'more' ? 'More' : 'Workspace';

  return (
    <>
      <IconRail
        slot={railSlot}
        onSelect={setRailSlot}
        collapsed={false}
        onExpand={() => setExpanded(true)}
        capabilities={capabilities}
        onOpenMore={(x, y) => setMoreMenuAt({ x, y })}
      />

      <div
        className="ez-pane"
        data-testid="sidebar-pane"
        data-overlay={asOverlay || undefined}
        style={{ ['--pane-width-current' as string]: `${width}px` }}
      >
        <div className="ez-pane__header">
          <h2 className="ez-pane__title">{paneTitle}</h2>
          {/*
            B10: monday's pane header is `···` · search · collapse with no title of its own
            beyond the section name `[c:sidebar]`; the `···` opens the 240 px workspace menu
            (`[c:sidebar-workspace-menu]`), which had been rendered `disabled` since F4.
          */}
          <IconButton
            icon={icons.moreActions}
            ariaLabel="Workspace actions"
            size="xs"
            kind="tertiary"
            data-testid="pane-menu"
            onClick={(event) => {
              const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
              setPaneMenuAt({ x: box.left, y: box.bottom + 4 });
            }}
          />
          <IconButton
            icon={icons.search}
            ariaLabel="Search the sidebar"
            size="xs"
            kind="tertiary"
            active={treeFilterOpen}
            onClick={() => setTreeFilterOpen(!treeFilterOpen)}
            data-testid="pane-filter-toggle"
          />
          <Tooltip content="Collapse the sidebar">
            <IconButton
              icon={icons.collapseLeft}
              ariaLabel="Collapse the sidebar"
              size="xs"
              kind="tertiary"
              onClick={() => setExpanded(false)}
              data-testid="pane-collapse"
            />
          </Tooltip>
        </div>

        {capabilities.workspaceSwitcher ? (
          <div className="ez-pane__switcher-row">
            <WorkspaceSwitcher
              current={payload?.workspace ?? null}
              canAddWorkspace={capabilities.canCreateWorkspace}
            />
            <button
              type="button"
              className="ez-switcher-add"
              aria-label="Add"
              // F4 owns the opener (§7.4); B1 owns every entry of the menu it opens.
              data-testid="pane-add"
              onClick={(event) => {
                const box = event.currentTarget.getBoundingClientRect();
                setAddMenuAt({ x: box.left - 180, y: box.bottom + 4 });
              }}
            >
              <Icon icon={icons.add} size={16} ignoreFocusStyle />
            </button>
          </div>
        ) : null}

        <div className="ez-pane__section">
          <span className="ez-pane__section-title">Content</span>
          {/* `[c:sidebar]`: the Content header carries its own 24×24 `···` and `+`. */}
          <IconButton
            icon={icons.moreActions}
            ariaLabel="Content section menu"
            size="xs"
            kind="tertiary"
            data-testid="content-section-menu"
            onClick={(event) => {
              const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
              setContentMenuAt({ x: box.left - 200, y: box.bottom + 4 });
            }}
          />
        </div>

        {workspaceId ? (
          <Link className="ez-pane__link" to={workspaceUrl(workspaceId)} data-testid="manage-workspace">
            <Icon icon={icons.workspace} size={16} ignoreFocusStyle />
            Manage workspace
          </Link>
        ) : null}

        {treeFilterOpen ? (
          <div className="ez-pane__filter">
            <Search
              placeholder="Filter"
              inputAriaLabel="Filter the sidebar"
              value={treeFilter}
              onChange={setTreeFilter}
              debounceMs={150}
              data-testid="pane-filter"
            />
          </div>
        ) : null}

        {railSlot === 'favorites' ? (
          <FavoritesPane />
        ) : sidebar.isPending && workspaceId ? (
          <div className="ez-pane__tree" aria-busy="true" data-testid="sidebar-skeleton">
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} type="rectangle" height={28} />
            ))}
          </div>
        ) : sidebar.isError ? (
          <PageErrorState
            kind="error"
            title="The sidebar could not load"
            hint="Retrying usually fixes it."
            action={
              <button type="button" className="ez-pane__link" onClick={() => sidebar.refetch()}>
                Retry
              </button>
            }
            data-testid="sidebar-error"
          />
        ) : (
          <SidebarTree
            rows={rows}
            expandedFolderIds={openFolderIds}
            onToggleFolder={toggleFolder}
            selectedId={selectedEntityId}
            workspaceId={workspaceId}
            onCollapseAllFolders={collapseAllFolders}
            emptyLabel={treeFilter ? 'No board matches that filter' : 'This workspace has no boards yet'}
          />
        )}

        <button
          type="button"
          className="ez-pane__resizer"
          aria-label="Resize the sidebar"
          onPointerDown={startResize}
          onDoubleClick={() => setWidth(SIDEBAR_WIDTH_DEFAULT)}
          data-testid="pane-resizer"
        />
      </div>

      {addMenuAt ? (
        <AddMenu
          workspaceId={workspaceId}
          x={addMenuAt.x}
          y={addMenuAt.y}
          onClose={() => setAddMenuAt(null)}
        />
      ) : null}

      {paneMenuAt ? (
        <WorkspaceMenu
          workspaceId={workspaceId}
          canCreateWorkspace={capabilities.canCreateWorkspace}
          x={paneMenuAt.x}
          y={paneMenuAt.y}
          onClose={() => setPaneMenuAt(null)}
        />
      ) : null}

      {moreMenuAt ? (
        <RailMoreMenu
          workspaceId={workspaceId}
          x={moreMenuAt.x}
          y={moreMenuAt.y}
          onClose={() => setMoreMenuAt(null)}
        />
      ) : null}

      {contentMenuAt ? (
        <ContentSectionMenu
          x={contentMenuAt.x}
          y={contentMenuAt.y}
          onCollapseAll={collapseAllFolders}
          sortAlphabetically={sortAlphabetically}
          onToggleSort={() => setContentSort(sortAlphabetically ? 'position' : 'alphabetical')}
          onClose={() => setContentMenuAt(null)}
        />
      ) : null}

      {asOverlay ? (
        <button
          type="button"
          className="ez-scrim"
          aria-label="Close the sidebar"
          onClick={onDismissOverlay}
          data-testid="pane-scrim"
        />
      ) : null}
    </>
  );
}
